/**
 * templates.ts — every call this app makes into the admin-versioned template catalogue.
 *
 * WHY THIS IS SEPARATE FROM `api.ts`. Everything in `api.ts` is about ONE DOCUMENT on one
 * contract. This module is about the WORDING every future document is generated from, and
 * the two have opposite lifetimes: a document is created, signed and sealed inside a
 * contract's setup, while a template version outlives every document built on it and is the
 * only artefact that says what somebody actually signed. The meaning of what comes back —
 * folding versions into families, spotting a document left behind on old wording — lives in
 * `templateCatalog.ts`, which has no client in its module graph and is unit-tested.
 *
 * ── READS ARE A DIRECT SELECT, WRITES ARE RPCS ────────────────────────────────
 * `agreement_templates` carries an admin-only SELECT policy (`is_admin()`) and a write
 * policy that is `USING (false) WITH CHECK (false)` for every client, so PostgREST can read
 * the catalogue and cannot write a byte of it. These three RPCs are the only door:
 *
 *   agreement_template_create_draft(key)   copies the latest version into a v N+1 draft
 *   agreement_template_update_draft(id, …) edits a draft — refused once published/referenced
 *   agreement_template_publish(id)         seals the draft and retires its predecessor
 *
 * TYPES ARE CAST ANYWAY. `types.ts` now carries `agreement_templates` and all three RPC
 * signatures, so the casts below are no longer standing in for a missing definition. They
 * stay because the generated types stop one level short of what this module promises: each
 * RPC is typed `Returns: Json`, and `parties` / `merge_fields` are `Json` columns, so every
 * call would need an assertion to the shape in `templateCatalog.ts` regardless. Casting the
 * client keeps that assertion at one point per call rather than at every field read — the
 * same shape `api.ts` uses, and the reason both files read alike.
 */

import { supabase } from '@/integrations/supabase/client';
import type { AgreementTemplateBody, AgreementTemplateSummary } from './templateCatalog';

const SUMMARY_COLUMNS =
    'id, template_key, version, doc_type, title, body_sha256, status, parties, ' +
    'merge_fields, effective_at, retired_at, created_at, updated_at';

// ─── Reads ───────────────────────────────────────────────────────────────────

/**
 * Every version of every template, newest version of each key first.
 *
 * Ordered in SQL rather than in the component because two consumers share this query key —
 * the templates page and the signing board's version hint — and a shared cache entry that
 * arrives in a different order on a refetch would answer the same question differently.
 */
export async function fetchAgreementTemplates(): Promise<AgreementTemplateSummary[]> {
    const { data, error } = await (supabase as any)
        .from('agreement_templates')
        .select(SUMMARY_COLUMNS)
        .order('template_key', { ascending: true })
        .order('version', { ascending: false });
    if (error) throw error;
    return (data ?? []) as AgreementTemplateSummary[];
}

export async function fetchAgreementTemplateBody(
    templateId: number,
): Promise<AgreementTemplateBody | null> {
    const { data, error } = await (supabase as any)
        .from('agreement_templates')
        .select(`${SUMMARY_COLUMNS}, body_md`)
        .eq('id', templateId)
        .maybeSingle();
    if (error) throw error;
    return (data ?? null) as AgreementTemplateBody | null;
}

// ─── Writes (all through the RPCs) ───────────────────────────────────────────

export interface TemplateDraftResult {
    template_id: number;
    template_key: string;
    version: number;
}

/** Copies the latest version of `templateKey` into a new editable draft at v N+1. */
export async function createTemplateDraft(templateKey: string): Promise<TemplateDraftResult> {
    const { data, error } = await (supabase as any).rpc('agreement_template_create_draft', {
        p_template_key: templateKey,
    });
    if (error) throw error;
    return data as TemplateDraftResult;
}

/**
 * Saves a draft's wording.
 *
 * `merge_fields` is deliberately NOT sent. That array is the PRE-FLIGHT REQUIREMENT LIST —
 * the fields a document may not be sent for signature without — and it is a curated subset
 * of what the body happens to mention, not a derivation of it. Recomputing it from the text
 * on every save would silently promote every incidental field into a blocker. The editor
 * shows what the body references instead, so a typo is visible without being enforced.
 */
export async function updateTemplateDraft(args: {
    templateId: number;
    bodyMd: string;
    title: string;
}): Promise<TemplateDraftResult> {
    const { data, error } = await (supabase as any).rpc('agreement_template_update_draft', {
        p_template_id: args.templateId,
        p_body_md: args.bodyMd,
        p_title: args.title,
    });
    if (error) throw error;
    return data as TemplateDraftResult;
}

/** Seals the draft as the published version and retires its predecessor. */
export async function publishTemplate(templateId: number): Promise<TemplateDraftResult> {
    const { data, error } = await (supabase as any).rpc('agreement_template_publish', {
        p_template_id: templateId,
    });
    if (error) throw error;
    return data as TemplateDraftResult;
}
